/**
 * WebSocket 消息处理模块
 * 将服务端消息路由到对应的 Zustand store 进行状态更新
 */
import type { ServerMessage } from '@fluxlab/protocols';
import { wsClient } from './client';
import { useConnectionStore } from '@/stores/connection';
import { useTelemetryStore } from '@/stores/telemetry';
import { usePidParamsStore } from '@/stores/pid-params';
import { useRecordingStore } from '@/stores/recording';
import { useFaultStore } from '@/stores/fault';
import { useAutoTuneStore } from '@/stores/auto-tune';
import { useIdentificationStore } from '@/stores/identification';
import type { TelemetryFrame } from '@fluxlab/protocols';

let unsub: (() => void) | null = null;
let telemetryQueue: TelemetryFrame[] = [];
let telemetryFlushTimer: ReturnType<typeof setTimeout> | null = null;

function queueTelemetry(frame: TelemetryFrame): void {
  if (telemetryQueue.length >= 1000) telemetryQueue = telemetryQueue.slice(-999);
  telemetryQueue.push(frame);
  if (telemetryFlushTimer) return;

  telemetryFlushTimer = setTimeout(() => {
    const frames = telemetryQueue;
    telemetryQueue = [];
    telemetryFlushTimer = null;
    useTelemetryStore.getState().addFrames(frames);
  }, 16);
}

/** 启动服务端消息处理器：订阅 wsClient 消息并路由到对应 Store */
export function startMessageHandler(): void {
  if (unsub) return;

  unsub = wsClient.subscribe((msg: ServerMessage) => {
    switch (msg.type) {
      case 'telemetry': {
        queueTelemetry(msg.payload);
        break;
      }
      case 'status': {
        useTelemetryStore.getState().setStatus(msg.payload);
        break;
      }
      case 'connection': {
        if (msg.payload.state === 'connected') {
          useConnectionStore.getState().setConnected(
            msg.payload.port || '',
            msg.payload.baudRate || 0
          );
        } else {
          useConnectionStore.getState().setDisconnected();
          useTelemetryStore.getState().setStreaming(false);
        }
        break;
      }
      case 'ports-list': {
        useConnectionStore.getState().setPorts(msg.payload);
        break;
      }
      case 'pid-params': {
        usePidParamsStore.getState().setParams(msg.payload.loop, msg.payload.params);
        break;
      }
      case 'pid-ack': {
        usePidParamsStore.getState().setLastAck(msg.payload);
        break;
      }
      case 'pid-history': {
        usePidParamsStore.getState().setHistory(msg.payload.loop, msg.payload.history);
        break;
      }
      case 'recording-started': {
        useRecordingStore.getState().setState('recording');
        useRecordingStore.getState().setActiveRecordingId(msg.payload.id);
        wsClient.send({ type: 'list-recordings' });
        break;
      }
      case 'recording-stopped': {
        useRecordingStore.getState().setState('stopped');
        useRecordingStore.getState().setActiveRecordingId(null);
        useRecordingStore.getState().updateRecording(msg.payload.id, {
          totalFrames: msg.payload.frames,
          stoppedAt: Date.now(),
        });
        wsClient.send({ type: 'list-recordings' });
        break;
      }
      case 'recording-list': {
        useRecordingStore.getState().setRecordings(msg.payload);
        break;
      }
      case 'recording-deleted': {
        useRecordingStore.getState().removeRecording(msg.payload.id);
        break;
      }
      case 'recording-exported': {
        break;
      }
      case 'recording-playback-started': {
        useRecordingStore.getState().beginPlayback(msg.payload.id);
        break;
      }
      case 'recording-playback-batch': {
        if (useRecordingStore.getState().playbackRecordingId === msg.payload.id) {
          useRecordingStore.getState().appendPlaybackFrames(msg.payload.frames);
        }
        break;
      }
      case 'recording-playback-completed': {
        if (useRecordingStore.getState().playbackRecordingId === msg.payload.id) {
          useRecordingStore.getState().completePlayback();
        }
        break;
      }
      case 'fault': {
        useFaultStore.getState().addRealtimeFault(msg.payload);
        break;
      }
      case 'auto-tune-progress': {
        useAutoTuneStore.getState().setProgress(
          msg.payload.step,
          msg.payload.total,
          msg.payload.message
        );
        break;
      }
      case 'auto-tune-result': {
        useAutoTuneStore.getState().setResult(msg.payload.pid, msg.payload.metrics);
        break;
      }
      case 'identification-progress': {
        useIdentificationStore.getState().setProgress(
          msg.payload.step,
          msg.payload.total,
          msg.payload.message
        );
        break;
      }
      case 'identification-result': {
        useIdentificationStore.getState().setResult(msg.payload);
        break;
      }
      case 'motor-params': {
        useIdentificationStore.getState().setMotorParams(msg.payload);
        break;
      }
      case 'error': {
        useConnectionStore.getState().setError(msg.payload.message);
        if (msg.payload.code === 'RECORDING_FAILED') {
          useRecordingStore.getState().setState('stopped');
          useRecordingStore.getState().setActiveRecordingId(null);
          wsClient.send({ type: 'list-recordings' });
        }
        // Route error to relevant stores if they're in running state
        if (useAutoTuneStore.getState().state === 'running') {
          useAutoTuneStore.getState().setError(msg.payload.message);
        }
        if (useIdentificationStore.getState().state === 'running') {
          useIdentificationStore.getState().setError(msg.payload.message);
        }
        console.error(`[WS Error] ${msg.payload.code}: ${msg.payload.message}`);
        break;
      }
      case 'ping': {
        wsClient.send({ type: 'pong', timestamp: Date.now() });
        break;
      }
      case 'pong': {
        break;
      }
    }
  });
}

/** 停止消息处理器：取消消息订阅（应用卸载时调用） */
export function stopMessageHandler(): void {
  if (unsub) {
    unsub();
    unsub = null;
  }
  if (telemetryFlushTimer) clearTimeout(telemetryFlushTimer);
  telemetryFlushTimer = null;
  telemetryQueue = [];
}
